import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CalendarClock, Plus, X, Repeat } from "lucide-react";

const WASTE_TYPES = ["general", "recyclable", "organic", "hazardous"];
const STATUSES = ["pending", "scheduled", "completed", "cancelled"];

const empty = {
  client_id: "",
  preferred_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
  waste_type: "general",
  estimated_weight_kg: "",
  notes: "",
};

export default function Pickups() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [autoBusy, setAutoBusy] = useState(false);
  const [weeks, setWeeks] = useState(2);

  const load = () => api.get("/pickups").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => {
      setClients(r.data);
      const m = {};
      r.data.forEach((c) => (m[c.id] = c.name));
      setClientMap(m);
    });
  }, []);

  const autoFill = async () => {
    setAutoBusy(true);
    try {
      const { data } = await api.post(`/pickups/generate-recurring?weeks=${weeks}`);
      if (data.created === 0 && data.skipped === 0) {
        toast.info("No sites have a recurring cadence set yet.");
      } else {
        toast.success(`Auto-filled ${data.created} pickup${data.created === 1 ? "" : "s"} · ${data.skipped} already existed`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Auto-fill failed");
    } finally {
      setAutoBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, estimated_weight_kg: form.estimated_weight_kg ? Number(form.estimated_weight_kg) : null };
      if (user?.role === "client") delete payload.client_id;
      await api.post("/pickups", payload);
      toast.success("Pickup requested");
      setForm(empty); setShowForm(false); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const updateStatus = async (p, s) => {
    await api.patch(`/pickups/${p.id}`, { status: s });
    toast.success(`Marked ${s}`);
    load();
  };

  const statusPill = (s) => {
    if (s === "pending") return "warn";
    if (s === "scheduled") return "forest";
    if (s === "completed") return "success";
    return "error";
  };

  return (
    <div>
      <PageHeader
        eyebrow="Scheduling"
        title="Pickup requests"
        subtitle={user?.role === "client" ? "Request a collection from HBT — we’ll respond within 24 hours." : "Manage incoming pickup requests from clients."}
        actions={
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <div className="flex items-center gap-2 mr-2 pr-3 border-r" style={{ borderColor: "var(--border)" }} data-testid="auto-fill-group">
                <label className="text-xs text-neutral-500 hidden sm:block">Next</label>
                <select
                  value={weeks}
                  onChange={(e) => setWeeks(Number(e.target.value))}
                  className="hbt-input py-1.5 text-xs max-w-[90px]"
                  data-testid="auto-fill-weeks"
                >
                  {[1, 2, 4, 8].map((w) => <option key={w} value={w}>{w} week{w > 1 ? "s" : ""}</option>)}
                </select>
                <button
                  onClick={autoFill}
                  disabled={autoBusy}
                  className="btn-outline inline-flex items-center gap-2"
                  data-testid="auto-fill-btn"
                  title="Generate pickup requests from each site's weekly cadence"
                >
                  <Repeat className={`w-4 h-4 ${autoBusy ? "animate-spin" : ""}`} /> {autoBusy ? "Filling…" : "Auto-fill"}
                </button>
              </div>
            )}
            <button onClick={() => { setForm({ ...empty, client_id: user?.role === "admin" ? (clients[0]?.id || "") : "" }); setShowForm(true); }} className="btn-primary inline-flex items-center gap-2" data-testid="new-pickup-btn">
              <Plus className="w-4 h-4" /> Request pickup
            </button>
          </div>
        }
      />
      <div className="px-8 lg:px-12 py-8">
        <div className="hbt-card overflow-hidden">
          <table className="hbt-table">
            <thead>
              <tr>
                <th>Preferred date</th>
                {user?.role === "admin" && <th>Client</th>}
                <th>Waste type</th>
                <th className="num">Est. weight</th>
                <th>Source</th>
                <th>Notes</th>
                <th>Status</th>
                {user?.role === "admin" && <th>Update</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid={`pickup-row-${p.id}`}>
                  <td className="font-mono-num">{p.preferred_date}</td>
                  {user?.role === "admin" && <td>{clientMap[p.client_id] || "—"}</td>}
                  <td><span className="pill forest">{p.waste_type}</span></td>
                  <td className="num font-mono-num">{p.estimated_weight_kg ? `${p.estimated_weight_kg} kg` : "—"}</td>
                  <td>
                    {p.source === "recurring"
                      ? <span className="pill forest inline-flex items-center gap-1" data-testid={`pickup-source-${p.id}`}><Repeat className="w-3 h-3" /> Auto</span>
                      : <span className="text-xs text-neutral-500" data-testid={`pickup-source-${p.id}`}>Manual</span>}
                  </td>
                  <td className="text-neutral-600 text-xs max-w-[240px] truncate">{p.notes || "—"}</td>
                  <td><span className={`pill ${statusPill(p.status)}`}>{p.status}</span></td>
                  {user?.role === "admin" && (
                    <td>
                      <select className="hbt-input text-xs py-1" value={p.status} onChange={(e) => updateStatus(p, e.target.value)} data-testid={`pickup-status-${p.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="text-center text-neutral-400 py-14">
                  <CalendarClock className="w-6 h-6 mx-auto mb-3 opacity-40" />
                  No pickup requests yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="hbt-card p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="pickup-form-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">Request</div>
                <div className="font-display text-2xl font-semibold mt-1">Schedule a pickup</div>
              </div>
              <button onClick={() => setShowForm(false)} className="text-neutral-400 hover:text-black"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              {user?.role === "admin" && (
                <div>
                  <label className="hbt-label block mb-2">Client</label>
                  <select required className="hbt-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="pickup-client-select">
                    <option value="">Select…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Preferred date</label>
                  <input type="date" required className="hbt-input" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} data-testid="pickup-date-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Waste type</label>
                  <select className="hbt-input" value={form.waste_type} onChange={(e) => setForm({ ...form, waste_type: e.target.value })} data-testid="pickup-type-select">
                    {WASTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="hbt-label block mb-2">Estimated weight (kg)</label>
                <input type="number" step="0.1" min="0" className="hbt-input" value={form.estimated_weight_kg} onChange={(e) => setForm({ ...form, estimated_weight_kg: e.target.value })} data-testid="pickup-weight-input" />
              </div>
              <div>
                <label className="hbt-label block mb-2">Notes</label>
                <textarea rows={3} className="hbt-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pickup-notes-input" />
              </div>
              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="submit-pickup">Submit request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
